import { SNAP_THRESHOLD } from '../stores/viewportStore';
import type { SnapGuide } from '../stores/viewportStore';

interface ElementBounds {
  id: string;
  left: number;
  top: number;
  right: number;
  bottom: number;
  centerX: number;
  centerY: number;
  width: number;
  height: number;
  source: 'artboard' | 'element';
}

interface SnapResult {
  x: number | null;
  y: number | null;
  guides: SnapGuide[];
}

export interface SnapTarget {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  source?: 'artboard' | 'element';
}

export const toBounds = (el: SnapTarget): ElementBounds => ({
  id: el.id,
  left: el.x,
  top: el.y,
  right: el.x + el.width,
  bottom: el.y + el.height,
  centerX: el.x + el.width / 2,
  centerY: el.y + el.height / 2,
  width: el.width,
  height: el.height,
  source: el.source ?? 'element',
});

export const calculateSnap = (
  moving: { x: number; y: number; width: number; height: number },
  others: SnapTarget[],
  gridSize: number,
  snapToGrid: boolean,
  snapToElements: boolean,
  artboard: { width: number; height: number } | null = null,
  threshold: number = SNAP_THRESHOLD
): SnapResult => {
  const guides: SnapGuide[] = [];
  let snapX: number | null = null;
  let snapY: number | null = null;
  let bestDx = threshold + 1;
  let bestDy = threshold + 1;

  const movingLeft = moving.x;
  const movingRight = moving.x + moving.width;
  const movingCenterX = moving.x + moving.width / 2;
  const movingTop = moving.y;
  const movingBottom = moving.y + moving.height;
  const movingCenterY = moving.y + moving.height / 2;

  const targets: ElementBounds[] = [];
  if (snapToElements) {
    targets.push(...others.map(toBounds));
    if (artboard) {
      targets.push(
        toBounds({
          id: '__artboard__',
          x: 0,
          y: 0,
          width: artboard.width,
          height: artboard.height,
          source: 'artboard',
        })
      );
    }
  }

  if (targets.length > 0) {
    for (const other of targets) {
      const xCandidates = [
        { movingEdge: movingLeft, otherEdge: other.left, type: 'edge' as const },
        { movingEdge: movingLeft, otherEdge: other.right, type: 'edge' as const },
        { movingEdge: movingRight, otherEdge: other.left, type: 'edge' as const },
        { movingEdge: movingRight, otherEdge: other.right, type: 'edge' as const },
        { movingEdge: movingCenterX, otherEdge: other.centerX, type: 'center' as const },
      ];

      for (const candidate of xCandidates) {
        const dist = Math.abs(candidate.movingEdge - candidate.otherEdge);
        if (dist < threshold && dist < bestDx) {
          bestDx = dist;
          if (candidate.movingEdge === movingLeft) {
            snapX = candidate.otherEdge;
          } else if (candidate.movingEdge === movingRight) {
            snapX = candidate.otherEdge - moving.width;
          } else {
            snapX = candidate.otherEdge - moving.width / 2;
          }
          const hGuides = guides.filter((g) => g.orientation === 'horizontal');
          guides.length = 0;
          guides.push(...hGuides);
          guides.push({
            orientation: 'vertical',
            position: candidate.otherEdge,
            type: candidate.type,
            source: other.source,
          });
        }
      }

      const yCandidates = [
        { movingEdge: movingTop, otherEdge: other.top, type: 'edge' as const },
        { movingEdge: movingTop, otherEdge: other.bottom, type: 'edge' as const },
        { movingEdge: movingBottom, otherEdge: other.top, type: 'edge' as const },
        { movingEdge: movingBottom, otherEdge: other.bottom, type: 'edge' as const },
        { movingEdge: movingCenterY, otherEdge: other.centerY, type: 'center' as const },
      ];

      for (const candidate of yCandidates) {
        const dist = Math.abs(candidate.movingEdge - candidate.otherEdge);
        if (dist < threshold && dist < bestDy) {
          bestDy = dist;
          if (candidate.movingEdge === movingTop) {
            snapY = candidate.otherEdge;
          } else if (candidate.movingEdge === movingBottom) {
            snapY = candidate.otherEdge - moving.height;
          } else {
            snapY = candidate.otherEdge - moving.height / 2;
          }
          const vGuides = guides.filter((g) => g.orientation === 'vertical');
          guides.length = 0;
          guides.push(...vGuides);
          guides.push({
            orientation: 'horizontal',
            position: candidate.otherEdge,
            type: candidate.type,
            source: other.source,
          });
        }
      }
    }
  }

  if (snapToGrid && gridSize > 0) {
    if (snapX === null) {
      const snappedLeft = Math.round(moving.x / gridSize) * gridSize;
      if (Math.abs(snappedLeft - moving.x) <= threshold) {
        snapX = snappedLeft;
        guides.push({ orientation: 'vertical', position: snappedLeft, type: 'grid', source: 'grid' });
      }
    }
    if (snapY === null) {
      const snappedTop = Math.round(moving.y / gridSize) * gridSize;
      if (Math.abs(snappedTop - moving.y) <= threshold) {
        snapY = snappedTop;
        guides.push({ orientation: 'horizontal', position: snappedTop, type: 'grid', source: 'grid' });
      }
    }
  }

  return { x: snapX, y: snapY, guides };
};

export const snapValueToGrid = (value: number, gridSize: number): number => {
  return Math.round(value / gridSize) * gridSize;
};

export const screenToArtboard = (
  screenX: number,
  screenY: number,
  zoom: number,
  panX: number,
  panY: number
): { x: number; y: number } => ({
  x: (screenX - panX) / zoom,
  y: (screenY - panY) / zoom,
});

export const artboardToScreen = (
  artboardX: number,
  artboardY: number,
  zoom: number,
  panX: number,
  panY: number
): { x: number; y: number } => ({
  x: artboardX * zoom + panX,
  y: artboardY * zoom + panY,
});
